import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation, useLocale } from "./contexts/LocaleContext";
import { getSortedCurrencies } from "./utils/currencyLocalization";
import { currencyNames } from "./currencyNames";
import { authenticatedFetch } from "./config/api";
import { 
  Play, 
  Pause, 
  Plus, 
  Trash2, 
  FolderPlus, 
  Clock, 
  FileText,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  Circle,
  CheckCircle,
  ChevronUp,
  DollarSign,
  Loader2
} from "lucide-react";

interface Task {
  id: string;
  name: string;
  rate: number;
  rateType: 'hourly' | 'daily' | 'fixed';
  seconds: number;
  isRunning: boolean;
  isBilled: boolean;
  isSelected?: boolean;
  lastStarted?: string; // ISO string from backend
  displaySeconds?: number; // UI-only
}

interface Project {
  id: string;
  name: string;
  tasks: Task[];
  isExpanded: boolean;
  currency?: string;
}

export default function TimeTracking() {
  const t = useTranslation() as any;
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newProjectName, setNewProjectName] = useState("");
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [activeCurrencyDropdown, setActiveCurrencyDropdown] = useState<string | null>(null);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);

  // Project name edit state
  const [editingProjectName, setEditingProjectName] = useState<{ id: string, value: string } | null>(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'project' | 'task', projectId: string, taskId?: string } | null>(null);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  // Manual time edit state
  const [editingTime, setEditingTime] = useState<{ projectId: string, taskId: string, value: string } | null>(null);

  // Sync state to avoid unnecessary re-renders
  const lastSyncRef = useRef<string>("");

  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Sync active sessions periodically
  useEffect(() => {
    const syncInterval = setInterval(async () => {
      try {
        const response = await authenticatedFetch('/api/time-tracking/active-sessions');
        if (response.ok) {
          const activeSessions = await response.json(); // [{taskId, startTime}, ...]
          const syncKey = JSON.stringify(activeSessions);
          
          // Only update if something changed on the server
          if (syncKey !== lastSyncRef.current) {
            lastSyncRef.current = syncKey;
            
            setProjects(prevProjects => {
              let hasChanges = false;
              const newProjects = prevProjects.map(project => {
                const newTasks = project.tasks.map(task => {
                  const serverSession = activeSessions.find((s: any) => s.taskId === task.id);
                  const shouldBeRunning = !!serverSession;
                  
                  if (task.isRunning !== shouldBeRunning) {
                    hasChanges = true;
                    return {
                      ...task,
                      isRunning: shouldBeRunning,
                      lastStarted: serverSession ? serverSession.startTime : null
                    };
                  }
                  return task;
                });
                return { ...project, tasks: newTasks };
              });

              if (hasChanges) {
                // If a task was stopped on another device, we need to fetch full data 
                // to get the updated 'seconds' count.
                fetchProjects();
              }
              return hasChanges ? newProjects : prevProjects;
            });
          }
        }
      } catch (error) {
        console.error("Sync error:", error);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(syncInterval);
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await authenticatedFetch('/api/time-tracking/projects');
      if (response.ok) {
        const data = await response.json();
        setProjects(data.map((p: any) => ({
          ...p,
          isExpanded: true,
          tasks: p.tasks.map((t: any) => ({
            ...t,
            rateType: t.rateType || 'hourly',
            displaySeconds: t.seconds
          }))
        })));
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Close currency dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(event.target as Node)
      ) {
        setActiveCurrencyDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Timer interval
  useEffect(() => {
    const interval = setInterval(() => {
      setProjects(prevProjects => 
        prevProjects.map(project => ({
          ...project,
          tasks: project.tasks.map(task => {
            if (task.isRunning && task.lastStarted) {
              const now = Date.now();
              const startedAt = new Date(task.lastStarted).getTime();
              const diff = Math.floor((now - startedAt) / 1000);
              return {
                ...task,
                displaySeconds: task.seconds + diff
              };
            }
            return { ...task, displaySeconds: task.seconds };
          })
        }))
      );
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Adjust height of all textareas
  useEffect(() => {
    projects.forEach(project => {
      project.tasks.forEach(task => {
        const el = textareaRefs.current[task.id];
        if (el) {
          el.style.height = 'auto';
          el.style.height = `${el.scrollHeight}px`;
        }
      });
    });
  }, [projects]);

  const addProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const response = await authenticatedFetch('/api/time-tracking/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newProjectName, currency: 'CZK' })
      });
      if (response.ok) {
        const newProject = await response.json();
        setProjects([{ ...newProject, tasks: [], isExpanded: true }, ...projects]);
        setNewProjectName("");
        setShowNewProjectModal(false);
      }
    } catch (error) {
      console.error("Error adding project:", error);
    }
  };

  const addTask = async (projectId: string) => {
    try {
      const response = await authenticatedFetch(`/api/time-tracking/projects/${projectId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: "", rate: 0, rateType: 'hourly' })
      });
      if (response.ok) {
        const newTask = await response.json();
        setProjects(projects.map(p => {
          if (p.id === projectId) {
            return { ...p, tasks: [...p.tasks, { ...newTask, isSelected: true, displaySeconds: 0 }], isExpanded: true };
          }
          return p;
        }));
      }
    } catch (error) {
      console.error("Error adding task:", error);
    }
  };

  const updateTask = async (projectId: string, taskId: string, updates: Partial<Task>) => {
    // Optimistic UI update
    setProjects(projects.map(p => {
      if (p.id === projectId) {
        return {
          ...p,
          tasks: p.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t)
        };
      }
      return p;
    }));

    try {
      await authenticatedFetch(`/api/time-tracking/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
    } catch (error) {
      console.error("Error updating task:", error);
    }
  };

  const toggleTimer = async (projectId: string, taskId: string) => {
    const project = projects.find(p => p.id === projectId);
    const task = project?.tasks.find(t => t.id === taskId);
    if (!task) return;

    const isStarting = !task.isRunning;
    
    // Optimistic UI update
    setProjects(projects.map(p => {
      if (p.id === projectId) {
        return {
          ...p,
          tasks: p.tasks.map(t => {
            if (t.id === taskId) {
              return {
                ...t,
                isRunning: isStarting,
                lastStarted: isStarting ? new Date().toISOString() : undefined
              };
            }
            return t;
          })
        };
      }
      return p;
    }));

    try {
      const endpoint = isStarting ? `/api/time-tracking/tasks/${taskId}/start` : `/api/time-tracking/tasks/${taskId}/stop`;
      const response = await authenticatedFetch(endpoint, { method: 'POST' });
      if (response.ok) {
        if (!isStarting) {
          fetchProjects(); // Sync final seconds
        } else {
          const result = await response.json();
          setProjects(prev => prev.map(p => {
            if (p.id === projectId) {
              return {
                ...p,
                tasks: p.tasks.map(t => t.id === taskId ? { ...t, lastStarted: result.start_time } : t)
              };
            }
            return p;
          }));
        }
      }
    } catch (error) {
      console.error("Error toggling timer:", error);
      fetchProjects();
    }
  };

  const formatTime = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map(v => v.toString().padStart(2, '0')).join(':');
  };

  const parseTimeToSeconds = (timeStr: string): number | null => {
    const parts = timeStr.split(':').map(Number);
    if (parts.length === 3 && parts.every(p => !isNaN(p) && p >= 0)) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2 && parts.every(p => !isNaN(p) && p >= 0)) {
      return parts[0] * 60 + parts[1];
    }
    const single = Number(timeStr);
    if (!isNaN(single) && single >= 0) return single;
    return null;
  };

  const handleTimeBlur = () => {
    if (editingTime) {
      const seconds = parseTimeToSeconds(editingTime.value);
      if (seconds !== null) {
        updateTask(editingTime.projectId, editingTime.taskId, { seconds });
      }
      setEditingTime(null);
    }
  };

  const handleProjectNameBlur = async () => {
    if (editingProjectName) {
      const trimmedName = editingProjectName.value.trim();
      if (trimmedName) {
        try {
          const response = await authenticatedFetch(`/api/time-tracking/projects/${editingProjectName.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: trimmedName })
          });
          if (response.ok) {
            setProjects(projects.map(p => p.id === editingProjectName.id ? { ...p, name: trimmedName } : p));
          }
        } catch (error) {
          console.error("Error updating project name:", error);
        }
      }
      setEditingProjectName(null);
    }
  };

  const calculateAmount = (task: Task) => {
    const seconds = task.displaySeconds ?? task.seconds;
    if (task.rateType === 'fixed') return task.rate;
    if (task.rateType === 'hourly') return (seconds / 3600) * task.rate;
    if (task.rateType === 'daily') return (seconds / (3600 * 8)) * task.rate;
    return 0;
  };

  const deleteProject = (id: string) => {
    setDeleteTarget({ type: 'project', projectId: id });
    setShowDeleteModal(true);
  };

  const deleteTask = (projectId: string, taskId: string) => {
    setDeleteTarget({ type: 'task', projectId, taskId });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'project') {
        const response = await authenticatedFetch(`/api/time-tracking/projects/${deleteTarget.projectId}`, { method: 'DELETE' });
        if (response.ok) setProjects(projects.filter(p => p.id !== deleteTarget.projectId));
      } else if (deleteTarget.type === 'task' && deleteTarget.taskId) {
        const response = await authenticatedFetch(`/api/time-tracking/tasks/${deleteTarget.taskId}`, { method: 'DELETE' });
        if (response.ok) {
          setProjects(projects.map(p => {
            if (p.id === deleteTarget.projectId) {
              return { ...p, tasks: p.tasks.filter(t => t.id !== deleteTarget.taskId) };
            }
            return p;
          }));
        }
      }
    } catch (error) {
      console.error("Error deleting:", error);
    }
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const generateInvoice = (project: Project) => {
    const selectedTasks = project.tasks.filter(t => t.isSelected);
    if (selectedTasks.length === 0) {
      alert(t.timeTracking.selectItems);
      return;
    }

    const items = selectedTasks.map(task => ({
      id: crypto.randomUUID(),
      description: task.name || "Service",
      quantity: task.rateType === 'fixed' ? 1 : (task.seconds / 3600),
      unit: task.rateType === 'fixed' ? 'ks' : 'h',
      amount: task.rate,
    }));

    const currencyCode = project.currency || "CZK";
    const currencyKey = Object.keys(currencyNames).find(key => currencyNames[key] === currencyCode);

    const invoiceDraft = {
      buyerName: project.name,
      items: items,
      currency: currencyCode,
      currencyKey: currencyKey || "",
      isFromTimeTracking: true,
      timeTrackingTaskIds: selectedTasks.map(t => t.id),
    };

    // We don't mark as billed here anymore. 
    // It will be done in InvoiceApp.tsx after the invoice is successfully saved.
    
    localStorage.setItem("invoice-draft-v3", JSON.stringify(invoiceDraft));
    localStorage.removeItem("invoice-internal-id");
    
    const path = locale === "cs" ? "/app" : `/${locale}/app`;
    navigate(path);
  };

  const updateProjectCurrency = async (projectId: string, currency: string) => {
    try {
      const response = await authenticatedFetch(`/api/time-tracking/projects/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currency })
      });
      if (response.ok) {
        setProjects(projects.map(p => p.id === projectId ? { ...p, currency } : p));
      }
    } catch (error) {
      console.error("Error updating currency:", error);
    }
    setActiveCurrencyDropdown(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <h1 className="text-3xl font-black text-slate-800 dark:text-white flex items-center gap-3">
          <Clock className="w-8 h-8 text-indigo-600" />
          {t.timeTracking.title}
        </h1>
        <button
          onClick={() => setShowNewProjectModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold transition-all shadow-lg shadow-indigo-500/20 border-0 outline-none"
        >
          <FolderPlus size={20} />
          {t.timeTracking.newProject}
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-[2.5rem] border-2 border-dashed border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-slate-300" />
          </div>
          <p className="text-slate-500 dark:text-slate-400 font-bold text-lg max-w-xs mx-auto">
            {t.timeTracking.noProjects}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {projects.map(project => (
            <div key={project.id} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div 
                className="p-5 bg-slate-50/50 dark:bg-slate-800/30 flex flex-col md:flex-row md:items-center justify-between cursor-pointer group gap-4"
                onClick={() => setProjects(projects.map(p => p.id === project.id ? { ...p, isExpanded: !p.isExpanded } : p))}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="p-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 flex-shrink-0">
                    {project.isExpanded ? <ChevronDown size={20} className="text-indigo-600" /> : <ChevronRight size={20} className="text-slate-400" />}
                  </div>
                  {editingProjectName?.id === project.id ? (
                    <input
                      autoFocus
                      type="text"
                      value={editingProjectName.value}
                      onChange={(e) => setEditingProjectName({ ...editingProjectName, value: e.target.value })}
                      onBlur={handleProjectNameBlur}
                      onKeyDown={(e) => e.key === 'Enter' && handleProjectNameBlur()}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xl font-black text-slate-800 dark:text-white bg-white dark:bg-slate-800 border border-indigo-300 dark:border-indigo-700 rounded-lg px-3 py-1 outline-none focus:ring-2 focus:ring-indigo-500 w-full"
                    />
                  ) : (
                    <h2 
                      className="text-xl font-black text-slate-800 dark:text-white group-hover:text-indigo-600 transition-colors cursor-pointer truncate"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingProjectName({ id: project.id, value: project.name });
                      }}
                    >
                      {project.name}
                    </h2>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative" ref={activeCurrencyDropdown === project.id ? currencyDropdownRef : null}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveCurrencyDropdown(activeCurrencyDropdown === project.id ? null : project.id);
                        }}
                        className="flex items-center gap-2 px-3 md:px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl font-black text-xs md:text-sm text-slate-700 dark:text-slate-200 hover:border-indigo-500 transition-all shadow-sm outline-none whitespace-nowrap"
                      >
                        <DollarSign className="w-3.5 h-3.5 md:w-4 md:h-4 text-indigo-500" />
                        {project.currency || "CZK"}
                        {activeCurrencyDropdown === project.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      <AnimatePresence>
                        {activeCurrencyDropdown === project.id && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl z-[100] overflow-hidden max-h-60 overflow-y-auto"
                          >
                            {getSortedCurrencies(locale).map(({ code, label }) => (
                              <button
                                key={code}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateProjectCurrency(project.id, code);
                                }}
                                className={`w-full text-left px-4 py-3 text-xs font-bold transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 border-0 outline-none bg-transparent ${
                                  (project.currency || "CZK") === code ? 'text-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20' : 'text-slate-600 dark:text-slate-400'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); generateInvoice(project); }}
                      className="flex items-center gap-2 px-3 md:px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-[10px] md:text-xs font-black transition-all border-0 outline-none whitespace-nowrap"
                    >
                      <FileText size={14} />
                      {t.timeTracking.generateInvoice}
                    </button>
                  </div>
                  
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                    className="p-2 text-slate-300 hover:text-red-500 transition-colors border-0 outline-none bg-transparent flex-shrink-0"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {project.isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="p-5 space-y-4">
                      {project.tasks.length === 0 ? (
                        <div className="text-center py-8 text-slate-400 font-medium text-sm">
                          {t.timeTracking.addService}
                        </div>
                      ) : (
                        project.tasks.map(task => (
                          <div key={task.id} className="flex flex-wrap lg:flex-nowrap items-center gap-4 p-4 bg-white dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 hover:border-indigo-100 dark:hover:border-indigo-900/50 transition-colors">
                            <button
                              onClick={() => updateTask(project.id, task.id, { isSelected: !task.isSelected })}
                              className="p-1 border-0 outline-none bg-transparent transition-colors cursor-pointer"
                            >
                              {task.isSelected ? <CheckCircle className="text-indigo-600" size={24} /> : <Circle className="text-slate-200 dark:text-slate-700" size={24} />}
                            </button>

                            <div className="flex-1 min-w-[180px]">
                              <textarea
                                ref={el => { textareaRefs.current[task.id] = el; }}
                                rows={1}
                                placeholder={t.timeTracking.serviceName}
                                value={task.name}
                                onChange={(e) => updateTask(project.id, task.id, { name: e.target.value })}
                                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all resize-none overflow-hidden block"
                                style={{ height: 'auto', minHeight: '38px' }}
                                onInput={(e) => {
                                  const target = e.target as HTMLTextAreaElement;
                                  target.style.height = 'auto';
                                  target.style.height = `${target.scrollHeight}px`;
                                }}
                              />
                            </div>
                            
                            <div className="flex items-center h-[38px] bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-1.5">
                              <input
                                type="number"
                                value={task.rate || ""}
                                onChange={(e) => updateTask(project.id, task.id, { rate: parseFloat(e.target.value) || 0 })}
                                className="w-20 bg-transparent border-0 outline-none text-sm font-black text-right pr-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="0"
                              />
                              <select
                                value={task.rateType}
                                onChange={(e) => updateTask(project.id, task.id, { rateType: e.target.value as any })}
                                className="bg-transparent border-0 outline-none text-[10px] text-slate-500 font-black uppercase tracking-tighter cursor-pointer pl-0 h-full appearance-none pr-4"
                                style={{ 
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right center',
                                  backgroundSize: '12px'
                                }}
                              >
                                <option value="hourly">{t.timeTracking.units.hourly}</option>
                                <option value="daily">{t.timeTracking.units.daily}</option>
                                <option value="fixed">{t.timeTracking.units.fixed}</option>
                              </select>
                            </div>

                            <div className="flex items-center gap-6 px-0">
                              <div className="text-center min-w-[90px]">
                                <div className="text-[9px] uppercase font-black tracking-[0.15em] text-slate-400 mb-1">Time</div>
                                {editingTime?.taskId === task.id ? (
                                  <input
                                    autoFocus
                                    type="text"
                                    value={editingTime.value}
                                    onChange={(e) => setEditingTime({ ...editingTime, value: e.target.value })}
                                    onBlur={handleTimeBlur}
                                    onKeyDown={(e) => e.key === 'Enter' && handleTimeBlur()}
                                    className="w-[95px] bg-slate-50 dark:bg-slate-900 border border-indigo-300 dark:border-indigo-700 rounded-lg px-1 py-0.5 font-mono text-base font-black text-center outline-none"
                                  />
                                ) : (
                                  <div 
                                    onClick={() => !task.isRunning && setEditingTime({ projectId: project.id, taskId: task.id, value: formatTime(task.displaySeconds ?? task.seconds) })}
                                    className={`font-mono text-base font-black transition-colors ${
                                      task.isRunning 
                                        ? 'text-indigo-600' 
                                        : 'text-slate-600 dark:text-slate-300 hover:text-indigo-500 cursor-pointer'
                                    }`}
                                  >
                                    {formatTime(task.displaySeconds ?? task.seconds)}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="flex flex-col items-center min-w-[100px]">
                                <div className="text-[9px] uppercase font-black tracking-[0.15em] text-slate-400 mb-1">{t.timeTracking.totalAmount}</div>
                                <div className="font-black text-base text-slate-800 dark:text-white leading-none" style={{ marginBottom: '0px', paddingBottom: '0px' }}>
                                  {calculateAmount(task).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="w-full" style={{ paddingTop: '0px', marginTop: '0px' }}>
                                  {task.isBilled ? (
                                    <div className="flex items-center justify-center gap-1 text-emerald-600 text-[8px] font-black uppercase bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-lg border border-emerald-100 dark:border-emerald-800/50">
                                      <CheckCircle2 size={10} />
                                      {t.timeTracking.billed}
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => updateTask(project.id, task.id, { isBilled: true, isSelected: false })}
                                      className="w-full text-[8px] font-black uppercase tracking-wider text-slate-400 hover:text-indigo-600 transition-colors border-0 outline-none bg-transparent p-0 m-0 leading-none"
                                      style={{ paddingTop: '0px', marginTop: '0px' }}
                                    >
                                      {t.timeTracking.notBilled}
                                    </button>
                                  )}
                                </div>
                              </div>

                              <button
                                onClick={() => toggleTimer(project.id, task.id)}
                                className={`p-3.5 rounded-2xl transition-all border-0 outline-none shadow-sm ${
                                  task.isRunning 
                                  ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-amber-500/20' 
                                  : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-500/20'
                                }`}
                              >
                                {task.isRunning ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
                              </button>
                              
                              <button
                                onClick={() => deleteTask(project.id, task.id)}
                                className="p-2 text-slate-300 hover:text-red-500 transition-colors border-0 outline-none bg-transparent"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                      
                      <button
                        onClick={() => addTask(project.id)}
                        className="w-full py-4 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-slate-400 hover:text-indigo-600 hover:border-indigo-200 dark:hover:border-indigo-900/50 transition-all flex items-center justify-center gap-2 font-black text-xs uppercase tracking-widest bg-transparent outline-none"
                      >
                        <Plus size={16} />
                        {t.timeTracking.addService}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}

      {/* New Project Modal */}
      <AnimatePresence>
        {showNewProjectModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowNewProjectModal(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[3rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 p-10"
            >
              <h3 className="text-3xl font-black text-slate-900 dark:text-white mb-8 tracking-tight">
                {t.timeTracking.newProject}
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 px-1">
                    {t.timeTracking.projectName}
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addProject()}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl px-6 py-4 text-lg font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    placeholder="e.g. Client Name"
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setShowNewProjectModal(false)}
                    className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-colors border-0 outline-none"
                  >
                    {t.common.cancel}
                  </button>
                  <button
                    onClick={addProject}
                    className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-500/25 border-0 outline-none"
                  >
                    {t.timeTracking.createProject}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[250]"
            onMouseDown={(e) => setMouseDownOnOverlay(e.target === e.currentTarget)}
            onMouseUp={(e) => {
              if (mouseDownOnOverlay && e.target === e.currentTarget) {
                setShowDeleteModal(false);
              }
              setMouseDownOnOverlay(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-slate-800 rounded-[2.5rem] p-8 max-w-md mx-4 shadow-2xl border border-slate-200/50 dark:border-slate-700/50 transition-colors"
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-pink-100 dark:from-red-900/30 dark:to-pink-900/30 rounded-2xl flex items-center justify-center shadow-sm">
                  <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tight">
                  {deleteTarget?.type === 'project' 
                    ? t.timeTracking.deleteModal.titleProject 
                    : t.timeTracking.deleteModal.titleTask}
                </h3>
              </div>

              <p className="text-slate-600 dark:text-slate-400 mb-8 text-base leading-relaxed font-medium">
                {deleteTarget?.type === 'project' 
                  ? t.timeTracking.deleteModal.messageProject 
                  : t.timeTracking.deleteModal.messageTask}
              </p>

              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-6 py-3 rounded-2xl border-2 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-bold transition-all border-0 outline-none bg-transparent"
                >
                  {t.timeTracking.deleteModal.cancel}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={confirmDelete}
                  className="flex-1 px-6 py-3 rounded-2xl bg-gradient-to-r from-red-600 to-pink-600 text-white font-bold transition-all shadow-lg shadow-red-500/30 border-0 outline-none"
                >
                  {t.timeTracking.deleteModal.confirm}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
